export type NativeState='unsupported'|'checking'|'disabled'|'off'|'enabled'|'blocked'|'waiting'|'timeout'|'error'
export interface Binding {deviceId:string;generation:string}
export interface NativePort {
 available:boolean
 loadBinding?:(user:string)=>Binding|null
 saveBinding?:(user:string,binding:Binding|null)=>void
 forgetDevice?:(user:string)=>void
 config:()=>Promise<boolean>
 check:()=>Promise<string>
 request:()=>Promise<string>
 register:()=>Promise<void>
 token:(fn:(value:string)=>void)=>Promise<()=>Promise<void>>
 error:(fn:()=>void)=>Promise<()=>Promise<void>>
 action:(fn:(target:string)=>void)=>Promise<()=>Promise<void>>
 resume:(fn:()=>void)=>Promise<()=>Promise<void>>
 attach:(deviceId:string,token:string)=>Promise<Binding>
 detach:(binding:Binding)=>Promise<void>
 deviceId:(user:string)=>string
 navigate:(target:string)=>void
}
/** Injectable native transport. No authenticator, permission or token is mocked in production. */
export class NativeLifecycle {
 private generation=0
 private user:string|null=null
 private removers:(()=>Promise<void>)[]=[]
 private timer:ReturnType<typeof setTimeout>|null=null
 private binding:Binding|null=null
 private pending:Promise<void>=Promise.resolve()
 private stopped=true
 private refreshing=false
 state:NativeState='unsupported'
 constructor(private port:NativePort,private change:(state:NativeState)=>void,private timeoutMs=15000){}
 private stateTo(state:NativeState){this.state=state;this.change(state)}
 private clearTimer(){if(this.timer)clearTimeout(this.timer);this.timer=null}
 async stop(){this.stopped=true;this.generation++;this.clearTimer();const old=this.removers.splice(0);await Promise.allSettled(old.map(remove=>remove()))}
 async start(user:string){
  const run=++this.generation;this.stopped=true;this.clearTimer();const old=this.removers.splice(0);await Promise.allSettled(old.map(remove=>remove()));if(run!==this.generation)return;this.user=user;this.stopped=false;this.refreshing=false;this.binding=this.port.loadBinding?.(user)??null
  if(!this.port.available){this.stateTo('unsupported');return}
  this.stateTo('checking')
  try{
   const results=await Promise.allSettled([this.port.token(value=>{this.pending=this.pending.catch(()=>{}).then(async()=>{if(this.stopped||run!==this.generation)return;this.clearTimer();try{const binding=await this.port.attach(this.port.deviceId(user),value);if(this.stopped||run!==this.generation){await this.port.detach(binding);return}this.binding=binding;this.port.saveBinding?.(user,binding);this.stateTo('enabled')}catch{if(!this.stopped&&run===this.generation)this.stateTo('error')}})}),this.port.error(()=>{if(run===this.generation){this.clearTimer();this.stateTo('error')}}),this.port.action(target=>{if(run===this.generation&&['#/mine','#/account','#/messages'].includes(target))this.port.navigate(target)}),this.port.resume(()=>{void this.refresh(false)})])
   const removers=results.flatMap(result=>result.status==='fulfilled'?[result.value]:[])
   if(results.some(result=>result.status==='rejected')){await Promise.allSettled(removers.map(fn=>fn()));throw Error('native_listeners')}
   if(this.stopped||run!==this.generation){await Promise.allSettled(removers.map(fn=>fn()));return}this.removers=removers
   await this.refresh(false)
  }catch{if(!this.stopped&&run===this.generation)this.stateTo('error')}
 }
 async enable(){if(this.user&&this.removers.length!==4)await this.start(this.user);await this.refresh(true)}
 private async refresh(explicit:boolean){
  if(!this.user||this.stopped||!this.port.available||this.refreshing)return
  this.refreshing=true
  const run=this.generation
  try{
   if(!await this.port.config()){if(run===this.generation)this.stateTo('disabled');return}
   let permission=await this.port.check()
   if(explicit&&permission!=='granted'&&permission!=='denied')permission=await this.port.request()
   if(this.stopped||run!==this.generation)return
   if(permission!=='granted'){this.stateTo(permission==='denied'?'blocked':'off');return}
   this.stateTo('waiting');this.clearTimer();this.timer=setTimeout(()=>{if(run===this.generation&&!this.stopped)this.stateTo('timeout')},this.timeoutMs)
   await this.port.register()
  }catch{if(run===this.generation&&!this.stopped){this.clearTimer();this.stateTo('error')}}finally{if(run===this.generation)this.refreshing=false}
 }
 /** Finish in-flight token writes before detaching, then let the session log out. */
 async logout(){await this.stop();await this.pending;if(this.binding){await this.port.detach(this.binding);this.binding=null}if(this.user)this.port.saveBinding?.(this.user,null);this.user=null;this.stateTo(this.port.available?'off':'unsupported')}
 async forget(){await this.stop();await this.pending;if(this.user){this.port.saveBinding?.(this.user,null);this.port.forgetDevice?.(this.user)}this.binding=null;this.user=null;this.stateTo(this.port.available?'off':'unsupported')}
}
